import {
  CreateInquiryType,
  RenderInquiryType,
  InquiryListType,
  InquiryPartnerCandidateType,
  InquiryType,
  UpdateInquiryType,
} from "types/InquiryTypes";
import { API_URL } from "./common";
import { FetchError } from "../exceptions";

const ENDPOINT = `${API_URL}/v1/ecommerce/inquiries`;

export const apiCreateInquiry = (
  token: string,
  inquiry: CreateInquiryType,
  sendEmail: boolean = true,
  sendFrom?: string
) => {
  const body = JSON.stringify(inquiry);
  return fetch(
    `${ENDPOINT}?sendEmail=${sendEmail}${
      sendFrom ? "&sendFrom=" + sendFrom : ""
    }`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: body,
    }
  ).then((res) => {
    return res.json();
  });
};

export const apiPreviewInquiry = (
  token: string,
  inquiry: RenderInquiryType,
  type: "pdf" | "html" = "html"
) => {
  const body = JSON.stringify(inquiry);
  return fetch(`${ENDPOINT}/render/${type}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body,
  });
};

export const apiPreviewInquiryFromId = (
  token: string,
  id: number,
  type: "pdf" | "html" = "html"
) => {
  const body = JSON.stringify(id);
  return fetch(`${ENDPOINT}/${id}/render/${type}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

// The inquiry as the server holds it. PUT /inquiries/<id> answers with the
// request body rather than the saved record, so this is how anything that has
// just written an inquiry gets the real one back.
export const apiGetInquiry = (
  token: string,
  id: number
): Promise<InquiryType> => {
  return fetch(`${ENDPOINT}/${id}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });
};

export const apiUpdateInquiry = (
  token: string,
  id: number,
  inquiry: UpdateInquiryType,
  sendEmail: boolean = true,
  sendFrom?: string
): Promise<void> => {
  const body = JSON.stringify(inquiry);
  return fetch(
    `${ENDPOINT}/${id}?sendEmail=${sendEmail}${
      sendFrom ? "&sendFrom=" + sendFrom : ""
    }`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: body,
    }
  ).then(async (res) => {
    // EE-253: this used to res.json() unconditionally. update_inquiry can now
    // reject an illegal status change with a 409 whose body is plain text, so
    // the parse threw an opaque SyntaxError instead of the server's reason.
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    // Nothing useful comes back: the route ends `return make_response(body,
    // 200)`, echoing what was sent. Read the inquiry with apiGetInquiry.
  });
};

/**
 * Sends the inquiry to its supplier. Its own endpoint rather than a flag on an
 * update, because sending is not a side effect of saving: send_inquiry mails
 * the supplier and, only if the inquiry is behind it, moves it to Sent /
 * Awaiting Response - which is also what carries the linked requisition to
 * Awaiting Supplier.
 *
 * It refuses a Draft (409 - approve it first), a closed inquiry (409) and one
 * whose supplier has no email address (400). Those come back as plain text, so
 * the body is the message worth showing.
 */
/**
 * `partnerIds`, when given, is who on the shortlist to write to this time -
 * it replaces each candidate's send-email switch for this one send and
 * leaves the switches as they were. Left out, the switches decide.
 *
 * `excludedFiles` are keys from apiGetInquiryAttachments to leave out of
 * this send. The Inquiry PDF itself always goes; it is not on that list.
 */
export const apiSendInquiry = (
  token: string,
  id: number,
  partnerIds?: number[],
  excludedFiles?: string[]
): Promise<void> => {
  const body: { partnerIds?: number[]; excludedFiles?: string[] } = {};
  if (partnerIds) body.partnerIds = partnerIds;
  if (excludedFiles && excludedFiles.length > 0)
    body.excludedFiles = excludedFiles;
  return fetch(`${ENDPOINT}/${id}/send`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
  });
};

/** One part file the next send would attach, as the server decides it. */
export type InquiryAttachmentType = {
  // What to hand back in `excludedFiles` to leave this one out.
  key: string;
  // The inquiry item the file belongs to, for grouping under its part.
  itemId: number;
  name: string;
  type: string;
};

/**
 * The part files a send of this inquiry attaches besides the Inquiry PDF -
 * models, drawings, uploads - decided per requisition type on the server,
 * so the confirmation shows exactly what would go out.
 */
export const apiGetInquiryAttachments = (
  token: string,
  id: number
): Promise<InquiryAttachmentType[]> =>
  fetch(`${ENDPOINT}/${id}/attachments`, {
    headers: { Authorization: `Bearer ${token}` },
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });

// The count behind the Inquiries menu badge: rows whose statusAckPending is
// set, i.e. changed from the requisition side and not opened since.
export const apiCountInquiryStatusUpdates = (
  token: string
): Promise<{ count: number }> =>
  fetch(`${ENDPOINT}/status-updates/count`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });

// Opening the inquiry is the acknowledgement. Idempotent server-side.
export const apiAcknowledgeInquiryStatus = (
  token: string,
  id: number
): Promise<InquiryType> =>
  fetch(`${ENDPOINT}/${id}/acknowledge-status`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });

/**
 * Convert the inquiry into a purchase order: settle the supplier, fix the
 * planned delivery date, store the note as the inquiry message, move to
 * PO Sent and email the purchase order - one request, all or nothing. It
 * stops there rather than at Ordered: the order has gone out, the supplier
 * has not answered yet. apiConfirmOrder is the other half. The server keeps
 * nothing if the email does not go out (502), so a failure leaves the
 * inquiry exactly where it was. 400 without a supplier or date, or if the
 * supplier has no address; 409 if the order already went out or the move is
 * not allowed from the current state. Recorded as STATUS_CHANGE then
 * SEND_PURCHASE_ORDER. `deadline` is YYYY-MM-DD.
 */
// The document headed as the purchase order, as it would go out if the
// inquiry were converted now - with the supplier, date and note the buyer is
// choosing in the conversion dialog. Read-only: the server renders from the
// saved inquiry and swaps the draft values in for the preview, writing
// nothing. Answers text/plain on failure, so a 200 is the only way an HTML
// email is told from an error rendered as one.
/** One priced line of a purchase order, in øre excl. VAT. */
export type InquiryItemPriceType = { itemId: number; priceCents: number };

export const apiPreviewPurchaseOrder = (
  token: string,
  id: number,
  body: {
    partnerId?: number;
    deadline?: string;
    message?: string;
    deliveryMethod?: string;
    itemPrices?: InquiryItemPriceType[];
    transportCostCents?: number;
  }
): Promise<Response> =>
  fetch(`${ENDPOINT}/${id}/render/purchase-order`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      partnerId: body.partnerId,
      deadline: body.deadline,
      message: body.message,
      deliveryMethod: body.deliveryMethod,
      itemPrices: body.itemPrices?.length ? body.itemPrices : undefined,
      transportCostCents: body.transportCostCents,
    }),
  });

export const apiConvertToPurchaseOrder = (
  token: string,
  id: number,
  body: {
    partnerId: number;
    deadline: string;
    message?: string;
    deliveryMethod: string;
    // Saved on the lines before the order is rendered, so the purchase order
    // prints them. A line left out keeps whatever price it already had.
    itemPrices?: InquiryItemPriceType[];
    transportCostCents?: number;
    excludedFiles?: string[];
  }
): Promise<void> =>
  fetch(`${ENDPOINT}/${id}/convert-to-purchase-order`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      partnerId: body.partnerId,
      deadline: body.deadline,
      message: body.message,
      deliveryMethod: body.deliveryMethod,
      itemPrices: body.itemPrices?.length ? body.itemPrices : undefined,
      transportCostCents: body.transportCostCents,
      excludedFiles: body.excludedFiles?.length ? body.excludedFiles : undefined,
    }),
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
  });

/**
 * Record the supplier's confirmation of the purchase order and move to
 * Ordered - the other half of apiConvertToPurchaseOrder, days later. Every
 * field is optional: an order confirmed exactly as it was sent is confirmed
 * by pressing the button, and only what the supplier changed is sent. The
 * supplier is not among them - a different supplier is a different order.
 * No email goes out; a revised purchase order is apiSendPurchaseOrder.
 * 409 from any state but PO Sent. Recorded as STATUS_CHANGE. `deadline` is
 * YYYY-MM-DD.
 */
export const apiConfirmOrder = (
  token: string,
  id: number,
  body: {
    deadline?: string;
    deliveryMethod?: string;
    itemPrices?: InquiryItemPriceType[];
    transportCostCents?: number;
    deliveryTime?: string;
    message?: string;
  }
): Promise<InquiryType> =>
  fetch(`${ENDPOINT}/${id}/confirm-order`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });

/**
 * The purchase order again: the inquiry document headed as an order, sent
 * to the settled supplier only. The first copy goes with
 * apiConvertToPurchaseOrder; this is the re-send. Allowed from PO Sent
 * onwards - 409 before, 409 once the inquiry is finished, 400 if the
 * supplier has no address. Recorded as SEND_PURCHASE_ORDER; does not touch
 * sentAt.
 */
export const apiSendPurchaseOrder = (
  token: string,
  id: number,
  excludedFiles?: string[]
): Promise<void> =>
  fetch(`${ENDPOINT}/${id}/purchase-order`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: excludedFiles?.length
      ? JSON.stringify({ excludedFiles })
      : undefined,
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
  });

/**
 * The OTHER action: record something that happened outside the flow - a chase
 * by phone, an agreement that moves nothing - and carry the inquiry to WIP if
 * it is not there yet. A Draft is deliberately left where it is: a note must
 * not be what approves something.
 *
 * Refuses an empty note (400) and a finished inquiry (409), as plain text.
 */
export const apiAddInquiryNote = (
  token: string,
  id: number,
  comment: string
): Promise<InquiryType> =>
  fetch(`${ENDPOINT}/${id}/note`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ comment }),
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });

/**
 * Record goods-in on one line while the inquiry is in Pickup / Delivery.
 * The counter sent is set, not added; the others stay.
 *
 * Moves the status when the counts say so: any of the quantity checked over
 * reads Ready to Use, and the inquiry comes back already moved. Completed is
 * the buyer's own step from there; a count corrected down under it takes it
 * away again. Arriving is not enough on its own - QC sits between being
 * here and being ready to use.
 *
 * The three counts are nested running totals, not a split of the line:
 * readyToUse <= receivedQty <= readyQty <= quantity. Ready to pick up is
 * everything the supplier announced and does not fall as pieces are
 * collected; raising Received past it raises it to match rather than
 * failing, so a buyer working from a collection note never keys the same
 * number twice.
 *
 * Refuses before Pickup / Delivery and once finished (409), and counts that
 * do not add up - over-delivery, ready on a delivery inquiry, ready past the
 * line, ready to use past what has arrived (400) - as plain text.
 */
export const apiUpdateInquiryItemDelivery = (
  token: string,
  id: number,
  itemId: number,
  counts: {
    receivedQty?: number;
    readyQty?: number;
    readyToUseQty?: number;
  }
): Promise<InquiryType> =>
  fetch(`${ENDPOINT}/${id}/items/${itemId}/delivery`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(counts),
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });

/**
 * Rejects the inquiry, and the requisition behind it with it. This is the way
 * out of a live inquiry - closing said only that it stopped, rejecting records
 * why and hands the decision back to the requester.
 *
 * Refuses a missing reason (400), a finished inquiry (409) and one past
 * Ordered (409 - it has been acted on in the world already).
 */
export const apiRejectInquiry = (
  token: string,
  id: number,
  comment: string
): Promise<InquiryType> =>
  fetch(`${ENDPOINT}/${id}/reject`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ comment }),
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });

/** Add a supplier to the inquiry's shortlist. Adding the same one twice is a
 * no-op server-side, so a double click cannot produce a second row that the
 * broadcast would then mail twice. */
export const apiAddInquiryCandidate = (
  token: string,
  inquiryId: number,
  partnerId: number,
  sendEmail: boolean = true
): Promise<InquiryPartnerCandidateType> =>
  fetch(`${ENDPOINT}/${inquiryId}/candidates`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ partnerId, sendEmail }),
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });

/** Turn the broadcast on or off for one supplier already on the shortlist.
 * Refused (409) once they have been sent the inquiry - nothing here can
 * unsend an email. */
export const apiSetInquiryCandidateSendEmail = (
  token: string,
  inquiryId: number,
  candidateId: number,
  sendEmail: boolean
): Promise<InquiryPartnerCandidateType> =>
  fetch(`${ENDPOINT}/${inquiryId}/candidates/${candidateId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ sendEmail }),
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
    return res.json();
  });

/** Remove a supplier from the shortlist. Refused (409) once they have been
 * sent the inquiry - their reply would arrive against a candidate that no
 * longer exists. */
export const apiRemoveInquiryCandidate = (
  token: string,
  inquiryId: number,
  candidateId: number
): Promise<void> =>
  fetch(`${ENDPOINT}/${inquiryId}/candidates/${candidateId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }).then(async (res) => {
    if (!res.ok) throw new FetchError(await res.text(), res.status);
  });

export type InquiryListScope = "mine" | "all";

// Narrows the closed inquiries: "cancelled" ended early, "finished" ran its
// course. Both are CLOSED; the backend tells them apart by the cancelled flag.
export type InquiryClosedAs = "cancelled" | "finished";

export const apiGetInquiries = (
  token: string,
  page: number,
  pageSize: number,
  sortKey: string = "id",
  sortDirection: "asc" | "desc" = "desc",
  status?: string,
  search?: string,
  filter?: string,
  // "mine": the inquiries this user raised, directly or through a
  // requisition. The backend's default is everything.
  scope?: InquiryListScope,
  closedAs?: InquiryClosedAs
): Promise<InquiryListType> => {
  return fetch(
    `${ENDPOINT}?page=${page}&pageSize=${pageSize}&sortKey=${sortKey}&sortDirection=${sortDirection}${
      // Encoded: a search holding "&" or "#" cut the query string short.
      search ? "&search=" + encodeURIComponent(search) : ""
    }${status ? "&status=" + status : ""}${filter ? "&filter=" + filter : ""}${
      scope ? "&scope=" + scope : ""
    }${closedAs ? "&closedAs=" + closedAs : ""}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  ).then((res) => {
    return res.json();
  });
};
